import { getStepPresentation } from './constants'
import type { TraceStep } from './types'

export function DetailPanel({ step }: { step: TraceStep | null }) {
  if (!step) {
    return <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.25)' }}>Selecciona un paso para ver detalle</div>
  }

  const presentation = getStepPresentation(step)
  const config = presentation.config
  const relatedFiles = presentation.relatedFiles
  const tools = presentation.tools
  const llmAnalysis = presentation.llmAnalysis

  const keyFacts: Array<{ label: string; value: string }> = []

  if (step.type === 'LANGGRAPH_NODE_ENTER' || step.type === 'LANGGRAPH_NODE_EXIT') {
    if (step.data.nodeName) keyFacts.push({ label: 'node', value: String(step.data.nodeName) })
    if (step.data.stageBefore) keyFacts.push({ label: 'stage before', value: String(step.data.stageBefore) })
    if (step.data.stageAfter) keyFacts.push({ label: 'stage after', value: String(step.data.stageAfter) })
    if (step.data.durationMs !== undefined) keyFacts.push({ label: 'duration', value: `${step.data.durationMs}ms` })
    if (step.data.error) keyFacts.push({ label: 'error', value: String(step.data.error) })
  }

  if (step.type === 'RUNTIME_ROUTING_DECISION' || step.type === 'RUNTIME_SWITCH') {
    if (step.data.runtime) keyFacts.push({ label: 'runtime', value: String(step.data.runtime) })
    if (step.data.direction) keyFacts.push({ label: 'direction', value: String(step.data.direction) })
    if (step.data.reason) keyFacts.push({ label: 'reason', value: String(step.data.reason) })
  }

  if (step.type === 'INITIAL_GRAPH_ROUTE' || step.type === 'FINAL_EFFECTIVE_ROUTE') {
    if (step.data.route) keyFacts.push({ label: 'route', value: String(step.data.route) })
    if (step.data.initialRoute) keyFacts.push({ label: 'initial route', value: String(step.data.initialRoute) })
    if (step.data.finalRoute) keyFacts.push({ label: 'final route', value: String(step.data.finalRoute) })
    if (step.data.escalationReason) keyFacts.push({ label: 'escalation reason', value: String(step.data.escalationReason) })
  }

  if (step.type === 'GRAPH_CHECKPOINT_LOAD' || step.type === 'GRAPH_CHECKPOINT_SAVE' || step.type === 'GRAPH_STATE_RESTORED' || step.type === 'GRAPH_CHECKPOINT_LOAD_FAILED') {
    if (step.data.threadId) keyFacts.push({ label: 'thread', value: String(step.data.threadId) })
    if (step.data.checkpointId) keyFacts.push({ label: 'checkpoint', value: String(step.data.checkpointId) })
    if (step.data.restored !== undefined) keyFacts.push({ label: 'restored', value: String(Boolean(step.data.restored)) })
    if (step.data.saved !== undefined) keyFacts.push({ label: 'saved', value: String(Boolean(step.data.saved)) })
    if (step.data.reason) keyFacts.push({ label: 'reason', value: String(step.data.reason) })
    if (step.data.error) keyFacts.push({ label: 'error', value: String(step.data.error) })
  }

  if (step.type === 'GRAPH_HITL_INTERRUPTED' || step.type === 'HITL_INTERRUPT_TRIGGERED' || step.type === 'HITL_INTERRUPT_RESUMED' || step.type === 'HITL_RESUME') {
    if (step.data.action) keyFacts.push({ label: 'action', value: String(step.data.action) })
    if (step.data.reason) keyFacts.push({ label: 'reason', value: String(step.data.reason) })
    if (step.data.stage) keyFacts.push({ label: 'stage', value: String(step.data.stage) })
    if (step.data.conversationId) keyFacts.push({ label: 'conversation', value: String(step.data.conversationId) })
    if (step.data.hitlRequest?.reason) keyFacts.push({ label: 'request reason', value: String(step.data.hitlRequest.reason) })
  }

  if (step.type === 'TOOL_EXECUTION' || step.type === 'TOOL_CALL' || step.type === 'CACHE') {
    if (step.data.toolName || step.data.tool) keyFacts.push({ label: 'tool', value: String(step.data.toolName || step.data.tool) })
    if (step.data.cacheHit !== undefined) keyFacts.push({ label: 'cache', value: step.data.cacheHit ? 'hit' : 'miss' })
    if (step.data.durationMs !== undefined) keyFacts.push({ label: 'duration', value: `${step.data.durationMs}ms` })
  }

  if (step.type === 'ESCALATION_REQUESTED' || step.type === 'ESCALATION_PROPAGATED' || step.type === 'ESCALATION_HANDOFF_TRIGGERED') {
    if (step.data.reason) keyFacts.push({ label: 'reason', value: String(step.data.reason) })
    if (step.data.source) keyFacts.push({ label: 'source', value: String(step.data.source) })
    if (step.data.messageId) keyFacts.push({ label: 'message', value: String(step.data.messageId) })
  }

  if (step.type === 'SUPERVISOR_REDIRECT' || step.type === 'SUPERVISOR_OVERRIDE') {
    if (step.data.source) keyFacts.push({ label: 'source', value: String(step.data.source) })
    if (step.data.reason) keyFacts.push({ label: 'reason', value: String(step.data.reason) })
    if (step.data.action) keyFacts.push({ label: 'action', value: String(step.data.action) })
    if (step.data.objectionSeverity) keyFacts.push({ label: 'severity', value: String(step.data.objectionSeverity) })
    if (step.data.objectionLevel !== undefined) keyFacts.push({ label: 'objection level', value: String(step.data.objectionLevel) })
    if (step.data.targetNode) keyFacts.push({ label: 'targetNode', value: String(step.data.targetNode) })
    if (step.data.instruction) keyFacts.push({ label: 'instruction', value: String(step.data.instruction) })
    if (step.data.conversationId) keyFacts.push({ label: 'conversation', value: String(step.data.conversationId) })
  }

  if (step.type === 'REENGAGEMENT_SUGGESTED' || step.type === 'REENGAGEMENT_SCHEDULED' || step.type === 'REENGAGEMENT_CANCELLED') {
    if (step.data.stage) keyFacts.push({ label: 'stage', value: String(step.data.stage) })
    if (step.data.stagnationTurns !== undefined) keyFacts.push({ label: 'stagnant turns', value: String(step.data.stagnationTurns) })
    if (step.data.timeoutMinutes !== undefined) keyFacts.push({ label: 'timeout', value: `${step.data.timeoutMinutes}m` })
    if (step.data.hasProductContext !== undefined) keyFacts.push({ label: 'product context', value: step.data.hasProductContext ? 'yes' : 'no' })
    if (step.data.onlyAfterPriceInquiry !== undefined) keyFacts.push({ label: 'price gate', value: step.data.onlyAfterPriceInquiry ? 'yes' : 'no' })
    if (step.data.reason) keyFacts.push({ label: 'reason', value: String(step.data.reason) })
    if (step.data.cancelledCount !== undefined) keyFacts.push({ label: 'cancelled', value: String(step.data.cancelledCount) })
  }

  return (
    <div style={{ padding: 20, height: '100%', overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${config.color}22`, border: `2px solid ${config.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
          {config.icon}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: config.color }}>{step.type}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{config.label}</div>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>+{step.ms}ms</div>
      </div>

      {tools.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Herramientas usadas</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {tools.map((tool, idx) => (
              <span key={`${tool}-${idx}`} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, background: 'rgba(99,102,241,0.16)', border: '1px solid rgba(99,102,241,0.26)', color: '#c4b5fd', fontFamily: 'monospace' }}>
                {tool}
              </span>
            ))}
          </div>
        </div>
      )}

      {keyFacts.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Hechos clave</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {keyFacts.map((fact) => (
              <div key={`${fact.label}-${fact.value}`} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>{fact.label}</div>
                <div style={{ fontSize: 12, color: '#e2e8f0', wordBreak: 'break-word', lineHeight: 1.45 }}>{fact.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(step.type === 'TOOL_CALL' || step.type === 'CACHE') && step.data.result !== undefined && (
        <div style={{ marginBottom: 16 }}>
          <details open>
            <summary style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer' }}>
              Resultado de la tool
            </summary>
            <pre style={{ fontSize: 12, color: '#34d399', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, padding: 14, margin: 0, overflowX: 'auto', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {JSON.stringify(step.data.result, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {step.type === 'LLM' && llmAnalysis.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Analisis del LLM</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {llmAnalysis.map((item) => (
              <div key={`${item.label}-${item.value.slice(0, 20)}`} style={{ background: 'rgba(236,72,153,0.06)', border: '1px solid rgba(236,72,153,0.16)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: '#f9a8d4', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.82)', lineHeight: 1.5, wordBreak: 'break-word' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Archivos relacionados</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {relatedFiles.map((file) => (
            <code key={file} style={{ fontSize: 11, color: '#93c5fd', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.18)', borderRadius: 6, padding: '8px 10px', wordBreak: 'break-all' }}>
              {file}
            </code>
          ))}
        </div>
      </div>

      {step.type === 'LLM' && step.data.rawResponse && (
        <div style={{ marginBottom: 16 }}>
          <details open>
            <summary style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', marginBottom: 8, cursor: 'pointer' }}>
              LLM RAW RESPONSE
            </summary>
            <pre style={{ fontSize: 12, color: '#ec4899', background: 'rgba(236,72,153,0.06)', border: '1px solid rgba(236,72,153,0.2)', borderRadius: 8, padding: 14, margin: 0, overflowX: 'auto', lineHeight: 1.7, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {(() => {
                try {
                  return JSON.stringify(JSON.parse(step.data.rawResponse), null, 2)
                } catch {
                  return step.data.rawResponse
                }
              })()}
            </pre>
          </details>
        </div>
      )}

      <details>
        <summary style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer' }}>
          Payload
        </summary>
        <pre style={{ fontSize: 12, color: '#a78bfa', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, padding: 14, margin: 0, overflowX: 'auto', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {JSON.stringify(step.data, null, 2)}
        </pre>
      </details>
    </div>
  )
}
